import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { login, user, refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      // Always refresh user state on login to get latest DB flags (e.g., real_email_verified)
      refreshUser().then(() => {
        navigate("/community");
      });
    }
  }, [user, navigate, refreshUser]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(email, password);
      navigate("/community");
    } catch (err) {
      const msg =
        err?.message ||
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "We couldn’t sign you in. Please check your details and try again.";
      setError(msg);
      // eslint-disable-next-line no-console
      console.error("[Login] Login failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-black via-slate-950 to-black flex items-center justify-center px-3 sm:px-4 py-6 sm:py-10">
      {/* darker, subtler haze (less colorful) */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-slate-400/5 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -right-24 h-80 w-80 rounded-full bg-emerald-500/5 blur-3xl" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.05),transparent_62%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(148,163,184,0.04),transparent_60%)]" />

      {/* BACK TO HOME */}
      <button
        onClick={() => navigate("/")}
        className="absolute top-3 left-3 sm:top-6 sm:left-6 inline-flex items-center gap-1.5 sm:gap-2
                   rounded-xl border border-white/10
                   bg-black/25 backdrop-blur-xl
                   px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium
                   text-white/85 hover:bg-white/[0.06]
                   transition active:scale-95"
      >
        <ArrowLeft size={16} className="sm:w-[18px] sm:h-[18px]" />
        <span className="hidden xs:inline">Back to Home</span>
        <span className="xs:hidden">Back</span>
      </button>

      <div className="max-w-5xl w-full grid gap-6 sm:gap-8 md:gap-10 md:grid-cols-[1.1fr,1fr] items-center">
        {/* LEFT SECTION */}
        <div className="space-y-4 sm:space-y-5 md:space-y-6 text-white">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/25 px-3 py-1 text-[10px] sm:text-xs font-medium text-white/80 backdrop-blur-xl">
            <span className="h-2 w-2 rounded-full bg-emerald-400/60" />
            Animehouse • Sign In
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-white/95">
            Welcome back to <span className="text-emerald-300/90">Animehouse</span>
          </h1>

          <p className="text-xs sm:text-sm md:text-base text-white/60 max-w-md">
            Jump back into your world—explore anime pages, share your work in{" "}
            <span className="text-white/85">ArtHouse</span>, and join
            discussions in the <span className="text-white/85">Community</span>.
          </p>

          <div className="grid grid-cols-1 xs:grid-cols-3 gap-2.5 sm:gap-3 md:gap-4 max-w-md text-xs md:text-sm">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl px-3 sm:px-4 py-2.5 sm:py-3">
              <p className="text-white/45 text-[10px] sm:text-xs">Discover</p>
              <p className="text-base sm:text-lg font-semibold text-white/90">
                Anime pages
              </p>
              <p className="mt-1 text-[10px] sm:text-xs text-white/45">
                Explore, favorite, review
              </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl px-3 sm:px-4 py-2.5 sm:py-3">
              <p className="text-white/45 text-[10px] sm:text-xs">ArtHouse</p>
              <p className="text-base sm:text-lg font-semibold text-white/90">
                Creator feed
              </p>
              <p className="mt-1 text-[10px] sm:text-xs text-white/45">
                Post, like, collect
              </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl px-3 sm:px-4 py-2.5 sm:py-3">
              <p className="text-white/45 text-[10px] sm:text-xs">Community</p>
              <p className="text-base sm:text-lg font-semibold text-white/90">
                Threads
              </p>
              <p className="mt-1 text-[10px] sm:text-xs text-white/45">
                Talk, build reputation
              </p>
            </div>
          </div>

          <p className="text-xs text-white/50">
            New to Animehouse?{" "}
            <Link
              to="/register"
              className="text-emerald-200/90 hover:text-emerald-100 underline-offset-2 hover:underline"
            >
              Create an account
            </Link>
            .
          </p>
        </div>

        {/* RIGHT SECTION – FORM */}
        <div
          className="relative rounded-3xl p-5 sm:p-6 md:p-7
                     border border-white/10
                     bg-white/[0.03]
                     backdrop-blur-2xl shadow-2xl shadow-black/60"
        >
          {/* inner ring + subtle top sheen */}
          <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-white/10" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-24 rounded-t-3xl bg-gradient-to-b from-white/[0.06] to-transparent" />

          <div className="mb-4 sm:mb-5 relative">
            <h2 className="text-lg sm:text-xl font-semibold text-white/95">
              Sign in
            </h2>
            <p className="text-[10px] sm:text-xs text-white/55 mt-1">
              Use your email and password to continue.
            </p>
          </div>

          {error && (
            <div className="mb-4 rounded-2xl border border-red-400/25 bg-red-500/10 px-3 py-2 text-xs text-red-100">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-3 relative">
            <label className="block">
              <span className="sr-only">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                required
                autoComplete="email"
                className="w-full rounded-xl border border-white/10 bg-black/25 px-3 py-2.5 text-sm
                           text-white/90 placeholder:text-white/30
                           focus:outline-none focus:ring-2 focus:ring-emerald-400/25
                           focus:border-emerald-400/20 transition"
              />
            </label>

            <div className="relative">
              <label className="block">
                <span className="sr-only">Password</span>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                  autoComplete="current-password"
                  className="w-full rounded-xl border border-white/10 bg-black/25 px-3 py-2.5 pr-11 text-sm
                             text-white/90 placeholder:text-white/30
                             focus:outline-none focus:ring-2 focus:ring-emerald-400/25
                             focus:border-emerald-400/20 transition"
                />
              </label>

              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center
                           rounded-lg border border-white/10 bg-white/[0.03] text-white/70
                           hover:bg-white/[0.06] hover:text-emerald-200 transition"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <div className="flex items-center justify-end">
              <Link
                to="/forgot-password"
                className="text-xs text-emerald-200/90 hover:text-emerald-100 underline-offset-2 hover:underline"
              >
                Forgot your password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`mt-4 w-full rounded-xl py-2.5 text-sm font-semibold
                          text-slate-950 transition active:scale-[0.98]
                          shadow-lg shadow-black/40 ${
                            loading
                              ? "bg-emerald-700/70 cursor-not-allowed"
                              : "bg-emerald-400/90 hover:bg-emerald-300/90"
                          }`}
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <p className="mt-4 text-[11px] text-white/55 text-center relative">
            Don&apos;t have an account?{" "}
            <Link
              to="/register"
              className="text-emerald-200/90 hover:text-emerald-100 underline-offset-2 hover:underline"
            >
              Create one
            </Link>
          </p>

          <p className="mt-3 text-[10px] text-white/40 text-center">
            By signing in, you agree to keep Animehouse respectful and
            community-first.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
